/**
 * Where each app lives: the HR apps on the shared origin — D-10 path-based
 * deploy mapping, the same prefixes each app declares as its Vite `base` and
 * that `functions/[[catchall]].js` rewrites at the edge — and Link Accounting
 * on its own host.
 *
 * `@linkhr/auth` is the one package that must sometimes send a user to a
 * DIFFERENT app than the one it is running inside: an admin demoted mid-session
 * (be-788 / CC-431) belongs in the employee app, and an invite accepted on the
 * wrong surface mints a session the other app owns. Same origin, so a hard
 * `window.location.assign` is the whole hand-off — the target app re-bootstraps
 * and re-authenticates from its own audience-scoped refresh credential.
 */
import type { Audience } from "../types.js";

/** Employee Web (`aud=mobile`). */
export const EMPLOYEE_APP_PATH = "/me/";
/** HR Admin (`aud=admin`). */
export const HR_ADMIN_APP_PATH = "/admin/";
/**
 * Link Accounting (`aud=accounting`) lives on its OWN host, so this is an
 * absolute URL: the accounting host of the HR zone the page is on. Its own
 * sign-in there mints its session (no hand-off carries one across hosts).
 * Production has no accounting host yet, so production and unknown hosts keep
 * the old same-origin path; add the production row when that host goes live.
 */
export function accountingAppUrl(hostname?: string): string {
  const host = hostname ?? (typeof window === "undefined" ? "" : window.location.hostname);
  if (host === "localhost" || host.endsWith(".localhost") || host === "127.0.0.1") {
    return "http://localhost:5177/";
  }
  if (host === "staging.hr.linkco.jo" || host.endsWith(".staging.hr.linkco.jo")) {
    return "https://app.staging.accounting.linkco.jo/";
  }
  return "/accounting/";
}
/** Link Admin (`aud=platform`) lives at the origin root. */
export const PLATFORM_APP_PATH = "/";
/**
 * The global login, at the root of the tenant-apps origin. It is where a page
 * goes when nothing names the company — the address does not (the global entry
 * host serves every company) and no session does either. Guessing a slug there
 * signs the visitor into someone else's company, so we ask instead.
 */
export const GLOBAL_LOGIN_PATH = "/";

/**
 * Base of the app that owns sessions for `aud`. Always trailing-slashed; a
 * path on this origin, except accounting's absolute URL on its own host.
 */
export function appBasePathForAudience(aud: Audience): string {
  switch (aud) {
    case "mobile":
      return EMPLOYEE_APP_PATH;
    case "admin":
      return HR_ADMIN_APP_PATH;
    case "platform":
      return PLATFORM_APP_PATH;
    case "accounting":
      return accountingAppUrl();
  }
}
